import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import storeJS from 'store';
import * as yup from 'yup';

import { apiUploadsS3 } from 'api/upload';
import {
  mutationEditorClear,
  mutationEditorQuestionCreate,
  mutationEditorQuestionPreview,
  mutationEditorQuestionPropose,
  mutationEditorQuestionTypeChange,
} from 'gql/mutations/editor';
import {
  useMutationQuestionApprove,
  useMutationQuestionCreate,
  useMutationQuestionDelete,
  useMutationQuestionImportFile,
  useMutationQuestionPropose,
  useMutationQuestionUpdate,
} from 'gql/mutations/question';
import { useQueryLocalEditor } from 'gql/queries/local';
import extract from 'helpers/extract';
import useModal from 'hooks/useModal';
import {
  getQuestionsInfo,
  getQuestionInfo,
  getQuestionEditor,
} from 'questions';
import { trackEvent, reportError } from 'lib/tracking';
import { notify } from 'uikit/Notifications';

import QuestionEdit from './QuestionEdit';

const defaultValues = {
  tmp: '',
  tags: [],
  title: '',
  answer: '',
  answerInput: '',
  answers: [],
  choices: [
    { idx: 0, title: '', answer: false, feedback: '' },
    { idx: 1, title: '', answer: false, feedback: '' },
  ],
  image: '',
  audio: '',
  video: '',
  file: '',
  items: [
    { idx: 0, title: '' },
    { idx: 1, title: '' },
  ],
  matches: [
    { idx: 0, source: '', destination: '' },
    { idx: 1, source: '', destination: '' },
  ],
  categories: [
    { idx: 0, source: '', destinations: [{ idx: 0, title: '' }] },
    { idx: 1, source: '', destinations: [{ idx: 0, title: '' }] },
  ],
  text: '',
  zones: [],
  legends: [],
  subtitle: '',
  additionalInformation: '',
  experts: [0, 0, 0, 0, 0],
  expertFeedbacks: [[], [], [], [], []],
  labels: ['', '', '', '', ''],
  feedback: '',
  feedbacks: false,
  slideContent: '',
  ignoreCase: true,
  ignoreAccents: true,
  ignorePunctuation: true,
  randomize: true,
  randomizeFlashcard: false,
  multipleChoices: true,
  wantPreview: false,
  background: 'white',
  textColor: 'black',
  toleranceValue: null,
  toleranceValueInput: '',
  toleranceType: 'NONE',
  chapterId: null,
  grade: null,
  studentFeedback: '',
  notifyStudent: true,
  titleLang: storeJS.get('titleLang') || 'fr',
  feedbackLang: storeJS.get('feedbackLang') || 'en-GB',
  useSpeech: storeJS.get('useSpeech') || true,
  answerType: 'video',
};
const QuestionEditContainer = ({ course, ...props }) => {
  const { t } = useTranslation();
  const {
    isModalOpen: isGradeModalOpen,
    handleModalOpen: handleGradeModalOpen,
    handleModalClose: handleGradeModalClose,
  } = useModal(false);
  const {
    isModalOpen: isDeleteModalOpen,
    handleModalOpen: handleDeleteModalOpen,
    handleModalClose: handleDeleteModalClose,
  } = useModal(false);
  const mutationQuestionApprove = useMutationQuestionApprove();
  const mutationQuestionCreate = useMutationQuestionCreate();
  const mutationQuestionPropose = useMutationQuestionPropose();
  const mutationQuestionUpdate = useMutationQuestionUpdate();
  const {
    data: {
      editor: {
        status: editorStatus,
        chapterSelected,
        questionType,
        questionEditing,
      },
    },
  } = useQueryLocalEditor();
  const chapterSelectedId = chapterSelected?.id || null;
  const questionInfo = getQuestionInfo(questionType);
  const editorComponent = getQuestionEditor(questionType);
  const validationSchema = useMemo(
    () => editorComponent?.validationSchema(t),
    [editorComponent, t],
  );
  const feedbackValidationSchema = useMemo(
    () =>
      yup.object().shape({
        grade: yup.number().integer().nullable(),
        studentFeedback: yup.string().nullable().trim(),
        notifyStudent: yup.boolean(),
      }),
    [],
  );
  const questionsInfo = getQuestionsInfo();
  const initialValues = useMemo(
    () =>
      questionEditing
        ? {
            ...defaultValues,
            choices:
              questionType === 'TrueOrFalse'
                ? [
                    { idx: 0, title: '', answer: false, feedback: '' },
                    { idx: 1, title: '', answer: false, feedback: '' },
                    { idx: 2, title: '', answer: false, feedback: '' },
                    { idx: 3, title: '', answer: false, feedback: '' },
                  ]
                : defaultValues.choices,
            ...questionEditing.content,
            tags: questionEditing.tags || [],
            title: questionEditing.title || '',
            experts: [
              ...(questionEditing.content.experts || []),
              0,
              0,
              0,
              0,
              0,
            ],
            expertFeedbacks: [
              ...(questionEditing.content.expertFeedbacks || []),
              [],
              [],
              [],
              [],
              [],
            ],
            titleLang:
              questionEditing.content.titleLang ||
              storeJS.get('titleLang') ||
              'fr',
            feedbackLang:
              questionEditing.content.feedbackLang ||
              storeJS.get('feedbackLang') ||
              'en-GB',
            useSpeech:
              questionEditing.content.useSpeech !== undefined
                ? questionEditing.content.useSpeech
                : storeJS.get('useSpeech') !== undefined
                ? storeJS.get('useSpeech')
                : true,
            background:
              questionEditing.content.background ||
              course.themeBackground ||
              'white',
            textColor: questionEditing.content.textColor || 'black',
          }
        : {},
    [course, questionEditing, questionType],
  );
  const handleSave = useCallback(
    async (values) => {
      try {
        const { tags, title, ...content } = extract(values, validationSchema);
        let question = null;
        if (editorStatus === 'question-create') {
          const {
            data: { questionCreate },
          } = await mutationQuestionCreate({
            courseId: course.id,
            chapterId: chapterSelectedId || values.chapterId,
            type: questionType,
            tags,
            title,
            content,
          });
          question = questionCreate;
          if (values.wantPreview) {
            notify(
              'success',
              t('Course/Teacher/Editor/QuestionEdit/create-success'),
            );
          }
          trackEvent('question', 'create');
        } else if (editorStatus === 'question-propose') {
          const {
            data: { questionPropose },
          } = await mutationQuestionPropose({
            courseId: course.id,
            type: questionType,
            tags,
            title,
            content,
          });
          question = questionPropose;
          notify(
            'success',
            t('Course/Teacher/Editor/QuestionEdit/propose-success'),
          );
          trackEvent('question', 'propose');
        } else if (editorStatus === 'question-approve') {
          const { grade, notifyStudent, studentFeedback } = extract(
            values,
            feedbackValidationSchema,
          );
          const {
            data: { questionApprove },
          } = await mutationQuestionApprove(questionEditing, {
            courseId: course.id,
            chapterId: chapterSelectedId || values.chapterId,
            type: questionType,
            tags,
            title,
            content,
            grade,
            studentFeedback,
            notifyStudent,
          });
          question = questionApprove;
          notify(
            'success',
            t('Course/Teacher/Editor/QuestionEdit/approve-success'),
          );
          handleGradeModalClose();
          trackEvent('question', 'approve');
        } else if (editorStatus === 'question-update') {
          const {
            data: { questionUpdate },
          } = await mutationQuestionUpdate(questionEditing, {
            type: questionType,
            tags,
            title,
            content,
          });
          question = questionUpdate;
          if (values.wantPreview) {
            notify(
              'success',
              t('Course/Teacher/Editor/QuestionEdit/update-success'),
            );
          }
          trackEvent('question', 'update');
        }
        if (values.wantPreview) {
          mutationEditorQuestionPreview(question);
        } else {
          mutationEditorClear();
        }
      } catch (err) {
        reportError('question', 'edition', err);
      }
    },
    [
      course,
      chapterSelectedId,
      editorStatus,
      feedbackValidationSchema,
      handleGradeModalClose,
      mutationQuestionApprove,
      mutationQuestionCreate,
      mutationQuestionPropose,
      mutationQuestionUpdate,
      questionEditing,
      questionType,
      t,
      validationSchema,
    ],
  );
  const handleRecreate = useCallback(
    (setErrors, setValues, submitForm, validateForm) => async () => {
      const errors = await validateForm();
      if (Object.keys(errors).length === 0) {
        await submitForm();
        await setValues(initialValues);
        if (editorStatus === 'question-create') {
          mutationEditorQuestionCreate(questionType);
        } else if (editorStatus === 'question-propose') {
          mutationEditorQuestionPropose(questionType);
        }
      } else {
        await setErrors(errors);
      }
    },
    [editorStatus, initialValues, questionType],
  );
  const handleTypeSelect = useCallback((e) => {
    mutationEditorQuestionTypeChange(e.target.value);
  }, []);
  const isModalOpen =
    editorStatus === 'question-create' ||
    editorStatus === 'question-propose' ||
    editorStatus === 'question-approve' ||
    editorStatus === 'question-update';
  const [askCloseValidation, setAskCloseValidation] = useState(false);
  const handleClose = useCallback(
    async (setValues, isDirty = false) => {
      if (isDirty && !askCloseValidation) {
        setAskCloseValidation(true);
      } else if (editorStatus === 'question-propose' && editorComponent) {
        mutationEditorQuestionPropose();
        await setValues(initialValues);
        setAskCloseValidation(false);
      } else if (questionType !== null && questionEditing.title === undefined) {
        mutationEditorQuestionCreate();
        await setValues(initialValues);
        setAskCloseValidation(false);
      } else {
        mutationEditorClear();
        setAskCloseValidation(false);
      }
    },
    [
      askCloseValidation,
      editorComponent,
      editorStatus,
      initialValues,
      questionEditing,
      questionType,
    ],
  );
  const handleCloseUndo = useCallback(() => {
    setAskCloseValidation(false);
  }, []);
  const mutationQuestionDelete = useMutationQuestionDelete();
  const handleDelete = useCallback(
    async (values) => {
      const { grade, notifyStudent, studentFeedback } = extract(
        values,
        feedbackValidationSchema,
      );
      await mutationQuestionDelete(questionEditing, {
        grade,
        studentFeedback,
        notifyStudent,
      });
      notify('success', t('Course/Teacher/Editor/QuestionEdit/delete-success'));
      handleDeleteModalClose();
      trackEvent('question', 'delete');
      mutationEditorClear();
    },
    [
      feedbackValidationSchema,
      handleDeleteModalClose,
      mutationQuestionDelete,
      questionEditing,
      t,
    ],
  );
  const mutationQuestionImportFile = useMutationQuestionImportFile();
  const handleQuestionsImport = useCallback(
    async (values, form) => {
      try {
        form.setStatus(null);
        const variables = extract(values, validationSchema);
        const extension = variables.file.name.split('.').slice(-1)[0];
        if (
          [
            'ppt',
            'pps',
            'pot',
            'pptx',
            'ppsx',
            'potx',
            'odp',
            'key',
            'pdf',
            'apkg',
          ].includes(extension)
        ) {
          const filePath = await apiUploadsS3(variables.file);
          const {
            data: { questionImportFile: questions },
          } = await mutationQuestionImportFile({
            ...variables,
            courseId: course.id,
            extension,
            fileName: variables.file.name,
            filePath,
          });
          if (Array.isArray(questions)) {
            if (!questions.length) {
              notify(
                'error',
                t('Course/Shared/QuestionEdit/question-import-error-empty'),
              );
            } else {
              notify(
                'success',
                t('Course/Shared/QuestionEdit/question-import-success', {
                  count: questions.length,
                }),
              );
            }
          }
          mutationEditorClear();
          trackEvent('question', 'import-file');
        }
      } catch (err) {
        form.setStatus('500');
        reportError('question', 'import-file', err);
      }
    },
    [course, mutationQuestionImportFile, validationSchema, t],
  );
  const canSelectChapter =
    (editorStatus === 'question-create' ||
      editorStatus === 'question-approve') &&
    questionInfo?.type !== 'PDFOrPPT' &&
    course.chapters.length > 0 &&
    !chapterSelectedId;
  return (
    <QuestionEdit
      askCloseValidation={askCloseValidation}
      canSelectChapter={canSelectChapter}
      course={course}
      editorComponent={editorComponent}
      editorStatus={editorStatus}
      handleClose={handleClose}
      handleCloseUndo={handleCloseUndo}
      handleDelete={handleDelete}
      handleDeleteModalClose={handleDeleteModalClose}
      handleDeleteModalOpen={handleDeleteModalOpen}
      handleGradeModalClose={handleGradeModalClose}
      handleGradeModalOpen={handleGradeModalOpen}
      handleQuestionsImport={handleQuestionsImport}
      handleRecreate={handleRecreate}
      handleSave={handleSave}
      handleTypeSelect={handleTypeSelect}
      initialValues={initialValues}
      isDeleteModalOpen={isDeleteModalOpen}
      isGradeModalOpen={isGradeModalOpen}
      isModalOpen={isModalOpen}
      questionInfo={questionInfo}
      questionsInfo={questionsInfo}
      t={t}
      validationSchema={validationSchema}
      {...props}
    />
  );
};

QuestionEditContainer.propTypes = {
  course: PropTypes.object.isRequired,
};

export default QuestionEditContainer;
